import { CategoriaEnum } from '@acme/auth'
import {
    Autocomplete,
    Button,
    ButtonGroup,
    CircularProgress,
    TextField,
} from '@mui/material'
import dayjs from 'dayjs'
import { useAuth, useUrlState } from 'hooks'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Funcionario, funcionario as funcionario_api } from 'service/api'
import { useStore } from 'store'
import { filterLocaisCentro } from './filter-select'
import { SelectProps } from './types'

interface IProps extends SelectProps {
    centroCustoId?: number
    localId?: number
    children?: never
    /**
     * Hook que se atualiza quando há ou não funcionários sendo listados no Select
     */
    hasFuncionariosRef?: React.MutableRefObject<boolean>
    /**
     * Callback para notificar quando a lista de funcionários está vazia ou preenchida.
     */
    onHasFuncionariosChange?: (hasFuncionarios: boolean) => void
}

export function SelectFuncionario({
    size = 'small',
    width = 260,
    button: hasButtons = false,
    disabled,
    centroCustoId,
    localId,
    variant = 'filled',
    hasFuncionariosRef,
    onHasFuncionariosChange,
}: IProps) {
    const {
        decoded: { categoria },
    } = useAuth()
    const diasRescisao = useStore(state => state.company?.diasRescisao)
    const funcionarioId = useStore(state => state.employeeId)
    const empresaId = useStore(state => state.companyId)
    const setEmployeeId = useStore(state => state.setEmployeeId)
    const [competencia, competenciaAtual] = useStore(state => [
        state.competencia,
        state.competenciaAtual,
    ])

    const [{ searchFuncionario }, setUrlState] = useUrlState({
        searchFuncionario: '',
    })
    const [inputValue, setInputValue] = useState(searchFuncionario)
    const debounceRef = useRef<ReturnType<typeof setTimeout>>()

    const handleInputChange = useCallback(
        (_: any, value: string, reason: string) => {
            if (reason === 'reset') {
                if (value) setInputValue(value)
                return
            }
            setInputValue(value)
            if (debounceRef.current) clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
                setUrlState({ searchFuncionario: value || (null as any) })
            }, 300)
        },
        [setUrlState],
    )

    useEffect(() => {
        setInputValue(searchFuncionario)
    }, [searchFuncionario])

    useEffect(() => {
        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current)
        }
    }, [])

    const queryParams: Record<string, unknown> = { empresaId }
    if (centroCustoId && centroCustoId > 0)
        queryParams.centroCustoId = centroCustoId
    if (localId && localId > 0) queryParams.localId = localId
    if (searchFuncionario) queryParams.search = searchFuncionario

    const {
        data: pages,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
        isFetching,
    } = funcionario_api.useInfiniteScroll({
        queryParams,
        enabled: empresaId > 0,
        perPage: 25,
    })

    const allLoaded = useMemo(
        () => (pages?.pages.flatMap(p => p.data) ?? []) as Funcionario[],
        [pages],
    )

    const exibirApenasAtivos = categoria !== CategoriaEnum.Funcionario
    const data = React.useMemo(
        () =>
            allLoaded.filter(obj => {
                if (exibirApenasAtivos && obj.dtRescisao) {
                    const data =
                            competencia === competenciaAtual
                                ? dayjs()
                                : dayjs(competencia).startOf('month'),
                        tolerancia = diasRescisao ?? 40

                    if (
                        data
                            .add(tolerancia, 'day')
                            .isAfter(dayjs(obj.dtRescisao), 'day')
                    )
                        return false
                }

                return filterLocaisCentro<typeof obj>(
                    !!localId && localId > 0 ? [localId] : [],
                    [centroCustoId!],
                    it => it,
                )(obj)
            }),
        [
            allLoaded,
            exibirApenasAtivos,
            centroCustoId,
            localId,
            competencia,
            competenciaAtual,
            diasRescisao,
        ],
    )

    const isPrimeiroFuncionario = useMemo(() => {
        if (data.length === 0) return true
        const index = data.findIndex(f => f.id === funcionarioId)
        return index === 0
    }, [data, funcionarioId])

    const isUltimoFuncionario = useMemo(() => {
        if (data.length === 0) return true
        const index = data.findIndex(f => f.id === funcionarioId)
        return index === data.length - 1 && !hasNextPage
    }, [data, funcionarioId, hasNextPage])

    const [isNavigating, setIsNavigating] = useState(false)

    async function next() {
        const index = data.findIndex(item => item.id === funcionarioId)
        if (index !== -1 && index < data.length - 1) {
            setEmployeeId(data[index + 1].id)
        } else if (hasNextPage) {
            setIsNavigating(true)
            const result = await fetchNextPage()
            setIsNavigating(false)
            const newItems = result.data?.pages.at(-1)?.data as
                | Funcionario[]
                | undefined
            if (newItems?.length) {
                setEmployeeId(newItems[0].id)
            }
        }
    }

    async function previous() {
        const index = data.findIndex(item => item.id === funcionarioId)
        if (index > 0) {
            setEmployeeId(data[index - 1].id)
        }
    }

    const selectedFuncionario = data.find(f => f.id === funcionarioId) || null

    const handleListScroll = useCallback(
        (event: React.SyntheticEvent) => {
            const listbox = event.currentTarget
            const nearBottom =
                listbox.scrollHeight -
                    listbox.scrollTop -
                    listbox.clientHeight <
                50
            if (nearBottom && hasNextPage && !isFetchingNextPage) {
                fetchNextPage()
            }
        },
        [fetchNextPage, hasNextPage, isFetchingNextPage],
    )

    // Set default employee if none selected
    useEffect(() => {
        const hasFuncionarios = data.length > 0

        if (hasFuncionariosRef?.current !== undefined) {
            hasFuncionariosRef.current = hasFuncionarios
        }

        onHasFuncionariosChange?.(hasFuncionarios)

        if (funcionarioId === -1 && data.length > 0) {
            setEmployeeId(data[0].id)
        }
    }, [data, funcionarioId, hasFuncionariosRef, onHasFuncionariosChange])

    return (
        <div className="flex items-center gap-2" key={selectedFuncionario?.id}>
            <Autocomplete
                className="flex-1"
                style={{ width }}
                options={data}
                getOptionLabel={option => option?.nome || ''}
                value={selectedFuncionario}
                onChange={(_, value) => {
                    if (value) {
                        setEmployeeId(value.id)
                        setUrlState({ searchFuncionario: null as any })
                    }
                }}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                filterOptions={x => x}
                loading={isFetching}
                loadingText={
                    <span className="text-muted-foreground select-none">
                        <CircularProgress size={16} className="mr-2" />
                        Carregando...
                    </span>
                }
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={disabled}
                ListboxProps={{ onScroll: handleListScroll } as any}
                renderInput={params => (
                    <TextField
                        {...params}
                        label="Funcionário"
                        size={size}
                        variant={variant}
                        className="w-full truncate"
                        InputLabelProps={{ shrink: true }}
                        placeholder="Selecione um funcionário"
                    />
                )}
                noOptionsText={
                    <span className="text-muted-foreground select-none">
                        Nenhum funcionário encontrado
                    </span>
                }
                clearIcon={null}
            />
            {hasButtons && (
                <ButtonGroup variant="outlined" className="h-full">
                    <Button
                        onClick={previous}
                        disabled={isPrimeiroFuncionario || isNavigating}
                        className="w-12 min-w-0 px-0"
                    >
                        <ChevronLeftIcon />
                    </Button>
                    <Button
                        onClick={next}
                        disabled={isUltimoFuncionario || isNavigating}
                        className="w-12 min-w-0 px-0"
                    >
                        <ChevronRightIcon />
                    </Button>
                </ButtonGroup>
            )}
        </div>
    )
}
